import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const intentLabels = {
  'custom-work': 'Build something specific for us',
  project: 'Start a custom software project',
  automation: 'Automate a workflow or process',
  edtech: 'Discuss education technology',
  demo: 'Request a product demo',
  academy: 'Discuss an Academy program',
  other: 'Something else',
};

const audienceLabels = {
  company: 'Company or institution',
  individual: 'Individual or founder',
};

const productLabels = {
  'path-view': 'Path View',
  restomatic: 'Restomatic',
};

const campaignKeys = ['utm_source', 'utm_medium', 'utm_campaign'];

export default function ContactMain() {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requestedIntent = params.get('intent');
  const requestedAudience = params.get('audience');
  const initialIntent = intentLabels[requestedIntent] ? requestedIntent : 'custom-work';
  const initialAudience = audienceLabels[requestedAudience] ? requestedAudience : 'company';
  const product = productLabels[params.get('product')] || '';
  const campaign = campaignKeys
    .map((key) => [key, params.get(key)])
    .filter(([, value]) => value);
  const [status, setStatus] = useState({ state: 'idle', message: '' });

  useEffect(() => {
    document.title = 'Contact | Nalexus Technologies';
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (status.state === 'submitting') return;

    const form = event.currentTarget;
    const values = new FormData(form);
    const audience = audienceLabels[values.get('audience')] || audienceLabels.company;
    const intent = intentLabels[values.get('intent')] || intentLabels.other;
    const details = [
      `Contact type: ${audience}`,
      `Request: ${intent}`,
      product ? `Product: ${product}` : null,
      campaign.length ? `Source: ${campaign.map(([key, value]) => `${key}=${value}`).join(', ')}` : null,
      '',
      String(values.get('message') || '').trim(),
    ].filter((line) => line !== null).join('\n');

    const payload = {
      firstName: String(values.get('firstName') || '').trim(),
      lastName: String(values.get('lastName') || '').trim(),
      company: String(values.get('organization') || '').trim(),
      email: String(values.get('email') || '').trim(),
      phone: String(values.get('phone') || '').trim(),
      message: details,
    };

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);

    setStatus({ state: 'submitting', message: 'Sending your enquiry securely…' });

    try {
      const response = await fetch('/api/forms/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.ok) {
        throw new Error(result.message || 'We could not send your enquiry. Please try again.');
      }

      form.reset();
      setStatus({
        state: 'success',
        message: 'Thank you — your enquiry has been received. The Nalexus team will review it and follow up using the details you provided.',
      });
    } catch (error) {
      const timedOut = error?.name === 'AbortError';
      setStatus({
        state: 'error',
        message: timedOut
          ? 'The request took too long. Please try again or email contact@nalexustechnologies.com.'
          : (error.message || 'We could not send your enquiry. Please try again or contact us by email.'),
      });
    } finally {
      window.clearTimeout(timeout);
    }
  };

  return (
    <main id="main-content" className="sync-main sync-subpage">
      <section className="sync-page-hero sync-contact-hero sync-section">
        <div className="sync-shell sync-page-hero-grid">
          <div>
            <p className="sync-eyebrow">Contact Nalexus</p>
            <h1>Start with the operation you want to improve.</h1>
            <p className="sync-lead">Whether you represent an organization or have an individual product idea, tell us what the software needs to make possible.</p>
          </div>
          <div className="sync-contact-direct">
            <p className="sync-kicker">Direct contact</p>
            <a href="mailto:contact@nalexustechnologies.com">contact@nalexustechnologies.com</a>
            <div>
              <a href="https://www.linkedin.com/company/nalexus-technologies/" target="_blank" rel="noreferrer">LinkedIn</a>
              <a href="https://www.instagram.com/nalexus_technologies/" target="_blank" rel="noreferrer">Instagram</a>
            </div>
          </div>
        </div>
      </section>

      <section className="sync-section sync-contact-section" aria-labelledby="contact-form-heading">
        <div className="sync-shell sync-contact-grid">
          <div className="sync-section-heading">
            <p className="sync-kicker">Project enquiry</p>
            <h2 id="contact-form-heading">Give us enough to make the first conversation useful.</h2>
            <p>Choose the type of enquiry and share the users, workflow, or outcome you have in mind. Required fields are marked with an asterisk.</p>
            <ul className="sync-contact-prompts">
              <li>Custom software or an internal business system</li>
              <li>AI automation or connected workflows</li>
              <li>Education technology or a product demonstration</li>
              <li>A specific idea your team needs designed and built</li>
            </ul>
          </div>

          <form className="sync-contact-form" onSubmit={handleSubmit}>
            {product && (
              <p className="sync-form-context">
                Product enquiry: <strong>{product}</strong>
              </p>
            )}

            <label>
              I am contacting as *
              <select name="audience" defaultValue={initialAudience} required>
                {Object.entries(audienceLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>

            <label>
              How can we help? *
              <select name="intent" defaultValue={initialIntent} required>
                {Object.entries(intentLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>

            <div className="sync-form-row">
              <label>
                First name *
                <input name="firstName" type="text" autoComplete="given-name" maxLength="80" required />
              </label>
              <label>
                Last name *
                <input name="lastName" type="text" autoComplete="family-name" maxLength="80" required />
              </label>
            </div>

            <div className="sync-form-row">
              <label>
                Email *
                <input name="email" type="email" autoComplete="email" maxLength="180" required />
              </label>
              <label>
                Phone <span className="sync-optional">(optional)</span>
                <input name="phone" type="tel" autoComplete="tel" maxLength="40" />
              </label>
            </div>

            <label>
              Company or institution <span className="sync-optional">(optional)</span>
              <input name="organization" type="text" autoComplete="organization" maxLength="180" />
            </label>

            <label>
              What should the system or service make possible? *
              <textarea
                name="message"
                rows="7"
                minLength="10"
                maxLength="4000"
                placeholder="For example: who will use it, the current process, the result you need, and any timing constraints."
                required
              />
            </label>

            <p className="sync-form-privacy">
              By submitting, you ask Nalexus Technologies to store these details and use them to respond to your enquiry. See our <Link to="/privacy-policy">Privacy Policy</Link>.
            </p>

            <button className="sync-button" type="submit" disabled={status.state === 'submitting'}>
              {status.state === 'submitting' ? 'Sending…' : 'Send enquiry'}
            </button>

            {status.message && (
              <p
                className={`sync-form-status sync-form-status-${status.state}`}
                role={status.state === 'error' ? 'alert' : 'status'}
              >
                {status.message}
              </p>
            )}
          </form>
        </div>
      </section>
    </main>
  );
}
